import { observer } from 'mobx-react-lite'
import { useFeatures } from '@/contexts/root-store-context'
import { useDocumentTitle } from '@/hooks/document-title'
import { useActivation } from '@/hooks/view-model-hooks'
import { ContentLoading } from '@/components/ContentLoading'
import { ContentError } from '@/components/ContentError'
import { WorkspaceSelectorViewHeadingContainer } from '@/features/iam/workspaces/components/WorkspaceSelectorViewHeading/WorkspaceSelectorViewHeadingContainer'
import { type InventoryManagerViewModel } from '@/features/inventory/admin/InventoryManagerViewModel'
import { InventoryManager } from '@/features/inventory/admin/InventoryManager'
import { InventoryCountLabel } from '@/features/inventory/admin/components/InventoryManagerHeader'
import { InventoryViewerContainer } from '@/features/inventory/admin/components/InventoryViewer/InventoryViewerContainer'
import { InventoryTableContainer } from '@/features/inventory/admin/components/InventoryTable/InventoryTableContainer'
import { CreateInventoryItemDialogContainer } from '@/features/inventory/admin/CreateInventoryItemDialog/CreateInventoryItemDialogContainer'
import { CreateWorkspaceInventoryItemDialogContainer } from '@/features/inventory/admin/CreateWorkspaceInventoryItemDialog/CreateWorkspaceInventoryItemDialogContainer'
import { EditInventoryItemDialogContainer } from '@/features/inventory/admin/EditInventoryItemDialog/EditInventoryItemDialogContainer'
import { OverrideInventoryItemDialogContainer } from '@/features/inventory/admin/OverrideInventoryItemDialog/OverrideInventoryItemDialogContainer'
import { ArchiveItemDialogContainer } from '@/features/inventory/admin/ArchiveItemDialog/ArchiveItemDialogContainer'
import { UnarchiveItemDialogContainer } from '@/features/inventory/admin/UnarchiveItemDialog/UnarchiveItemDialogContainer'

/**
 * The admin service manager page. Admin mounts it behind its own permission
 * check.
 */
export default observer(function InventoryManagerPageContainer() {
  const { inventory } = useFeatures((r) => ({
    inventory: r.inventory.internal(),
  }))
  return <InventoryManagerContainer vm={inventory.inventoryManagerViewModel} />
})

export interface InventoryManagerContainerProps {
  vm: InventoryManagerViewModel
}

export const InventoryManagerContainer = observer(
  function InventoryManagerContainer({ vm }: InventoryManagerContainerProps) {
    useDocumentTitle('Service Manager')
    useActivation(vm)

    return vm.fetch.match({
      pending: () => <ContentLoading />,
      rejected: (err) => <ContentError error={err} />,
      resolved: () => <InventoryManagerContentContainer vm={vm} />,
    })
  },
)

const InventoryManagerContentContainer = observer(
  function InventoryManagerContentContainer({
    vm,
  }: InventoryManagerContainerProps) {
    return (
      <InventoryManager
        heading={
          <WorkspaceSelectorViewHeadingContainer
            primaryText={'Service manager'}
            allOptionDropdownItem={
              vm.canManageGlobalInventory ? (
                <InventoryCountLabel
                  label="Global inventory"
                  count={vm.globalInventoryCount}
                />
              ) : null
            }
            alwaysUseDropdown={vm.workspacesUseDropdown}
            workspaces={vm.workspaces}
            dropdownTitle="Select inventory"
            onChange={vm.selectWorkspaceForFiltering}
            searcher={vm.canSearch ? vm.workspaceSearcher : undefined}
            renderName={(workspace) => {
              return (
                <InventoryCountLabel
                  label={workspace.name}
                  count={vm.getWorkspaceInventoryItemsCount(workspace)}
                />
              )
            }}
            allOptionText={
              vm.canManageGlobalInventory ? 'Global Inventory' : null
            }
            value={vm.selectedWorkspace}
          />
        }
        canManageGlobalInventory={vm.canManageGlobalInventory}
        canManageWorkspaceInventory={vm.canManageWorkspaceInventory}
        onAddGlobalItem={vm.addGlobalInventoryItem}
        onAddWorkspaceItem={vm.addWorkspaceInventoryItem}
        viewer={
          <InventoryViewerContainer filter={vm.searcher.inventoryTypeFilter} />
        }
        searchText={vm.searcher.searchText.value}
        onSearchTextChange={vm.searcher.searchText.change}
        isGlobalInventory={vm.globalSelected}
        onAddInventoryItem={vm.addInventoryItem}
        table={<InventoryTableContainer vm={vm} />}
      >
        <CreateInventoryItemDialogContainer vm={vm.createInventoryItemVm} />
        <CreateWorkspaceInventoryItemDialogContainer
          vm={vm.createWorkspaceInventoryItemVm}
        />
        <EditInventoryItemDialogContainer vm={vm.editInventoryItemVm} />
        <OverrideInventoryItemDialogContainer vm={vm.overrideInventoryItemVm} />
        <ArchiveItemDialogContainer vm={vm.archiveItemVm} />
        <UnarchiveItemDialogContainer vm={vm.unarchiveItemVm} />
      </InventoryManager>
    )
  },
)
